import { STORAGE_KEYS } from '@/constants/storage'
import { CreateVaultStep, IToken } from '@/types'
import { EphemeralWalletSigner } from '@/utils'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

interface PersistState {
  fromToken: IToken | null
  toToken: IToken | null
  isBridge: boolean
  setFromToken: (token: IToken) => void
  setToToken: (token: IToken) => void
  setIsBridge: (isBridge: boolean) => void
}

const initializeData = {
  fromToken: null,
  toToken: null,
  isBridge: false,
}

export const useTradePersistStore = create<PersistState>()(
  persist(
    (set) => ({
      ...initializeData,
      setFromToken: (fromToken) => set({ fromToken }),
      setToToken: (toToken) => set({ toToken }),
      setIsBridge: (isBridge) => set({ isBridge }),
    }),
    {
      name: 'trade-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
)

type EphemeralKeyMap = {
  [tradeId: string]: EphemeralWalletSigner
}

export interface TradeState {
  depositId: string | null
  setDepositId: (sessionId: string) => void

  currentStep: CreateVaultStep
  failedStep: CreateVaultStep | null
  setCurrentStep: (currentStep: CreateVaultStep) => void
  setFailedStep: (failedStep: CreateVaultStep | null) => void

  ephemeralKeys: EphemeralKeyMap
  setSigner: (tradeId: string, ephemeralInfo: EphemeralWalletSigner) => void
  getSigner: (tradeId: string) => EphemeralWalletSigner | undefined

  showConfirmScreen: boolean
  setShowConfirmScreen: (show: boolean) => void

  resetTradeStore: () => void
}

const depositIdStorage = {
  getDepositId: () => {
    return sessionStorage.getItem(STORAGE_KEYS.DEPOSIT_ID) ?? ''
  },
  setDepositId: (depositId: string) => {
    sessionStorage.setItem(STORAGE_KEYS.DEPOSIT_ID, depositId)
  },
  removeDepositId: () => {
    sessionStorage.removeItem(STORAGE_KEYS.DEPOSIT_ID)
  },
}

const initializeTradeData = {
  depositId: depositIdStorage.getDepositId(), // Fixed bug: when refresh page, the depositId is not set
  currentStep: CreateVaultStep.INIT,
  failedStep: null,
  showConfirmScreen: false,
}
export const useTradeStore = create<TradeState>((set, get) => ({
  ...initializeTradeData,
  ephemeralKeys: {},
  setSigner: (tradeId: string, ephemeralInfo: EphemeralWalletSigner) => {
    set((state) => ({
      ephemeralKeys: {
        ...state.ephemeralKeys,
        [tradeId]: ephemeralInfo,
      },
    }))
  },
  getSigner: (tradeId: string) => {
    return get().ephemeralKeys[tradeId]
  },
  setDepositId: (depositId) => {
    depositIdStorage.setDepositId(depositId)
    set({ depositId })
  },
  setCurrentStep: (currentStep) => set({ currentStep }),
  setFailedStep: (failedStep) => set({ failedStep }),
  resetTradeStore: () => {
    depositIdStorage.removeDepositId()
    set({ ...initializeTradeData })
  },
  setShowConfirmScreen: (show) => set({ showConfirmScreen: show }),
}))
export const useShowConfirmTransactionScreen = () => useTradeStore((state) => state.showConfirmScreen)
export const useSetShowConfirmTransactionScreen = () => useTradeStore((state) => state.setShowConfirmScreen)
